import React from 'react';
import PropTypes from 'prop-types';

import {EditorState} from './editor-state';
import {AppStateProviderHOC} from './app-state-provider-hoc';

import {
    setProjectChanged,
    setProjectUnchanged
} from '../reducers/project-changed';

/**
 * Higher Order Component to provide redux state. If an `intl` prop is provided
 * it will override the internal `intl` redux state
 * @param {React.Component} WrappedComponent - component to provide state for
 * @param {boolean} localesOnly - only provide the locale state, not everything
 *                      required by the GUI. Used to exclude excess state when
 *                      only rendering modals, not the GUI.
 * @param {GUIConfigFactory} configFactory - The configuration to use.
 * @returns {React.Component} component with redux and intl state provided
 */
const AppStateHOC = function (WrappedComponent, localesOnly, configFactory) {
    const AppStateProvider = AppStateProviderHOC(WrappedComponent);

    class AppStateWrapper extends React.Component {
        constructor (props) {
            super(props);

            this.appState = new EditorState({
                localesOnly,
                isFullScreen: props.isFullScreen,
                isPlayerOnly: props.isPlayerOnly,
                showTelemetryModal: props.showTelemetryModal,
                isEmbedded: props.isEmbedded
            }, configFactory);
this.handleMoodleMessage = this.handleMoodleMessage.bind(this);

this.moodleSaveInProgress = false;

        }


componentDidMount () {
    window.addEventListener(
        'message',
        this.handleMoodleMessage
    );

    this.lastMoodleProjectChanged =
        this.appState.store.getState()
            .scratchGui.projectChanged;

    this.unsubscribeMoodleProjectChanged =
        this.appState.store.subscribe(() => {

            const state =
                this.appState.store.getState();

            const projectChanged =
                state.scratchGui.projectChanged;

            if (
                projectChanged ===
                this.lastMoodleProjectChanged
            ) {
                return;
            }

            this.lastMoodleProjectChanged =
                projectChanged;

            if (
                projectChanged &&
                window.parent !== window
            ) {
                window.parent.postMessage(
                    {
                        type:
                            'scratch-project-changed'
                    },
                    'https://moodle.flcorp.fr'
                );
            }
        });
}


componentWillUnmount () {
    window.removeEventListener(
        'message',
        this.handleMoodleMessage
    );

    if (this.unsubscribeMoodleProjectChanged) {
        this.unsubscribeMoodleProjectChanged();
    }
}


async handleMoodleMessage (event) {
    const moodleOrigin = 'https://moodle.flcorp.fr';

    if (event.origin !== moodleOrigin) {
        return;
    }

    if (window.parent === window || !event.data) {
        return;
    }

    const state = this.appState.store.getState();
    const vm = state.scratchGui && state.scratchGui.vm;

    if (!vm) {
        console.error('Scratch : VM indisponible.');
        return;
    }

    // -----------------------------------------------------
    // Sauvegarde vers Moodle
    // -----------------------------------------------------


if (event.data.type === 'scratch-request-save') {

    const currentState =
        this.appState.store.getState();

    const projectChanged =
        currentState.scratchGui &&
        currentState.scratchGui.projectChanged;

const forceSave =
    event.data.force === true;

if (!projectChanged && !forceSave) {
    console.log(
        'Scratch : aucune modification, sauvegarde ignorée.'
    );
    return;
}

    if (this.moodleSaveInProgress) {
        console.log(
            'Scratch : sauvegarde déjà en cours.'
        );
        return;
    }

    if (typeof vm.saveProjectSb3 !== 'function') {
        console.error(
            'Scratch : fonction de sauvegarde indisponible.'
        );
        return;
    }

    try {
        this.moodleSaveInProgress = true;

        /*
         * Le projet courant devient notre nouvelle référence.
         * Si l'élève modifie ensuite quelque chose pendant
         * l'envoi, Scratch repassera automatiquement
         * projectChanged à true.
         */
        this.appState.store.dispatch(
            setProjectUnchanged()
        );

        const project =
            await vm.saveProjectSb3();

        window.parent.postMessage(
            {
                type: 'scratch-save-project',
                project,
                reason: event.data.reason || 'autosave'
            },
            moodleOrigin
        );

        console.log(
            'Scratch : projet envoyé à Moodle.'
        );

    } catch (error) {

        this.moodleSaveInProgress = false;

        this.appState.store.dispatch(
            setProjectChanged()
        );

        console.error(
            'Scratch : erreur lors de la création du projet SB3.',
            error
        );
    }

    return;
}



if (event.data.type === 'scratch-save-confirmed') {

    this.moodleSaveInProgress = false;

    console.log(
        'Scratch : sauvegarde confirmée par Moodle.'
    );

    return;
}

if (event.data.type === 'scratch-save-failed') {

    this.moodleSaveInProgress = false;

    this.appState.store.dispatch(
        setProjectChanged()
    );

    console.error(
        'Scratch : Moodle n’a pas pu enregistrer le projet.'
    );

    return;
}





    // -----------------------------------------------------
    // Chargement depuis Moodle
    // -----------------------------------------------------

    if (event.data.type === 'scratch-load-project') {

        if (typeof vm.loadProject !== 'function') {
            console.error(
                'Scratch : fonction de chargement indisponible.'
            );
            return;
        }

        if (!event.data.project) {
            console.error(
                'Scratch : aucune donnée de projet à charger.'
            );
            return;
        }

        try {
            await vm.loadProject(event.data.project);

            console.log(
                'Scratch : projet chargé depuis Moodle.'
            );

            window.parent.postMessage(
                {
                    type: 'scratch-project-loaded'
                },
                moodleOrigin
            );

        } catch (error) {
            console.error(
                'Scratch : erreur lors du chargement du projet.',
                error
            );
        }
    }
}




        render () {
            return (
                <AppStateProvider
                    appState={this.appState}
                    localesOnly={localesOnly}
                    {...this.props}
                />
            );
        }
    }
    AppStateWrapper.propTypes = {
        isFullScreen: PropTypes.bool,
        isPlayerOnly: PropTypes.bool,
        isTelemetryEnabled: PropTypes.bool,
        showTelemetryModal: PropTypes.bool,
        isEmbedded: PropTypes.bool
    };
    return AppStateWrapper;
};

export default AppStateHOC;
